import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Table, Drawer, SearchBar, BadgePill, EmptyState } from '@/components/campus';
import {
  MessageSquare,
  AlertCircle,
  Clock,
  CheckCircle,
  User,
  Building2,
  Calendar,
  MoreHorizontal,
  Eye,
  Loader2,
  HelpCircle,
  Inbox,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format helpers
const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatTimeAgo = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
};

// Status colors
const statusConfig = {
  open: { label: 'Open', variant: 'warning', icon: AlertCircle },
  in_progress: { label: 'In Progress', variant: 'primary', icon: Clock },
  resolved: { label: 'Resolved', variant: 'success', icon: CheckCircle },
  closed: { label: 'Closed', variant: 'muted', icon: CheckCircle },
};

// Priority colors
const priorityConfig = {
  low: { label: 'Low', variant: 'default' },
  medium: { label: 'Medium', variant: 'warning' },
  high: { label: 'High', variant: 'error' },
  urgent: { label: 'Urgent', variant: 'error' },
};

// ==================== TICKET DETAIL DRAWER ====================
const TicketDetailDrawer = ({ ticket, isOpen, onClose }) => {
  if (!ticket) return null;

  const status = statusConfig[ticket.status] || statusConfig.open;
  const priority = priorityConfig[ticket.priority] || priorityConfig.medium;
  const StatusIcon = status.icon;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Support Ticket" width="lg">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-xl bg-soft-amber/10 flex items-center justify-center">
            <MessageSquare className="w-8 h-8 text-soft-amber" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-cloud-white">{ticket.subject}</h3>
            <p className="text-soft-silver text-sm mt-1">Ticket #{ticket.id?.slice(0, 8) || '—'}</p>
            <div className="flex items-center gap-2 mt-2">
              <BadgePill variant={status.variant}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {status.label}
              </BadgePill>
              <BadgePill variant={priority.variant}>{priority.label} Priority</BadgePill>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 bg-obsidian/50 rounded-lg border border-white/[0.06]">
            <User className="w-5 h-5 text-cool-mint" />
            <div>
              <p className="text-sm text-muted-text">Submitted By</p>
              <p className="text-cloud-white">{ticket.user_email || ticket.user_name || '—'}</p>
            </div>
          </div>
          
          {ticket.organization_name && (
            <div className="flex items-center gap-3 p-3 bg-obsidian/50 rounded-lg border border-white/[0.06]">
              <Building2 className="w-5 h-5 text-electric-ice-blue" />
              <div>
                <p className="text-sm text-muted-text">Organization</p>
                <p className="text-cloud-white">{ticket.organization_name}</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 p-3 bg-obsidian/50 rounded-lg border border-white/[0.06]">
            <Calendar className="w-5 h-5 text-soft-amber" />
            <div>
              <p className="text-sm text-muted-text">Created</p>
              <p className="text-cloud-white font-mono">{formatDate(ticket.created_at)}</p>
            </div>
          </div>
        </div>

        {/* Message */}
        <div className="p-4 bg-obsidian/50 rounded-lg border border-white/[0.06]">
          <p className="text-sm text-muted-text mb-2">Message</p>
          <p className="text-soft-silver whitespace-pre-wrap">{ticket.message || ticket.description || 'No message provided.'}</p>
        </div>

        {/* Action Buttons Placeholder */}
        <div className="flex gap-3 pt-4 border-t border-white/[0.06]">
          <Button
            variant="outline"
            className="flex-1 border-white/[0.08]"
            onClick={() => toast.info('Response system coming soon')}
          >
            Reply
          </Button>
          <Button
            className="flex-1 bg-cool-mint text-white hover:bg-cool-mint/90"
            onClick={() => toast.info('Status update coming soon')}
          >
            Mark Resolved
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

// ==================== EMPTY STATE FOR SUPPORT ====================
const SupportEmptyState = () => (
  <div className="py-16 text-center">
    <div className="w-20 h-20 rounded-2xl bg-cool-mint/10 flex items-center justify-center mx-auto mb-6">
      <Inbox className="w-10 h-10 text-cool-mint" />
    </div>
    <h3 className="text-xl font-semibold text-cloud-white mb-2">All caught up!</h3>
    <p className="text-soft-silver max-w-md mx-auto">
      No support tickets at the moment. When users submit support requests, they'll appear here.
    </p>
    <div className="mt-8 p-4 bg-obsidian/50 rounded-xl border border-white/[0.06] max-w-md mx-auto">
      <p className="text-sm text-muted-text mb-2">
        <HelpCircle className="w-4 h-4 inline mr-1" />
        Support system is ready
      </p>
      <p className="text-xs text-muted-text">
        Users can submit tickets via the help button in their dashboard, or email support directly.
      </p>
    </div>
  </div>
);

// ==================== MAIN SUPPORT PAGE ====================
export const Support = () => {
  const { session } = useAuth();
  
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  // Fetch tickets
  const fetchTickets = useCallback(async () => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/platform/support/tickets?page_size=100`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setTickets(data.tickets || []);
      }
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Filter tickets
  const filteredTickets = tickets.filter(t => {
    const matchesSearch = 
      t.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.organization_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Stats
  const openTickets = tickets.filter(t => t.status === 'open').length;
  const inProgressTickets = tickets.filter(t => t.status === 'in_progress').length;

  // Table columns
  const columns = [
    {
      key: 'subject',
      label: 'Ticket',
      render: (_, row) => (
        <div className="flex items-center gap-3">
          <div className={cn(
            "w-10 h-10 rounded-lg flex items-center justify-center",
            row.priority === 'high' || row.priority === 'urgent' 
              ? "bg-signal-red/10" 
              : "bg-soft-amber/10"
          )}>
            <MessageSquare className={cn(
              "w-5 h-5",
              row.priority === 'high' || row.priority === 'urgent'
                ? "text-signal-red"
                : "text-soft-amber"
            )} />
          </div>
          <div>
            <p className="font-medium text-cloud-white">{row.subject || 'No Subject'}</p>
            <p className="text-xs text-muted-text">{row.user_email || 'Unknown user'}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      align: 'center',
      render: (value) => {
        const config = statusConfig[value] || statusConfig.open;
        return <BadgePill variant={config.variant}>{config.label}</BadgePill>;
      },
    },
    {
      key: 'priority',
      label: 'Priority',
      align: 'center',
      render: (value) => {
        const config = priorityConfig[value] || priorityConfig.medium;
        return <BadgePill variant={config.variant}>{config.label}</BadgePill>;
      },
    },
    {
      key: 'created_at',
      label: 'Submitted',
      render: (value) => (
        <span className="text-soft-silver font-mono text-sm">{formatTimeAgo(value)}</span>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (_, row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="w-4 h-4 text-muted-text" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-graphite border-white/[0.08]">
            <DropdownMenuItem 
              onClick={() => { setSelectedTicket(row); setIsDetailOpen(true); }}
              className="text-cloud-white hover:bg-white/[0.05]"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Details
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-soft-amber animate-spin" />
          <p className="text-soft-silver text-sm">Loading support tickets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="platform-support-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-cloud-white">Support</h1>
          <p className="text-soft-silver text-sm mt-1">
            {tickets.length === 0 
              ? 'No support tickets' 
              : `${tickets.length} tickets • ${openTickets} open • ${inProgressTickets} in progress`
            }
          </p>
        </div>
        
        {/* Status Filter */}
        {tickets.length > 0 && (
          <div className="flex gap-2">
            {['all', 'open', 'in_progress', 'resolved'].map((status) => (
              <Button
                key={status}
                variant={statusFilter === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter(status)}
                className={cn(
                  statusFilter === status 
                    ? 'bg-electric-ice-blue text-white' 
                    : 'border-white/[0.08] text-soft-silver'
                )}
              >
                {status === 'all' ? 'All' : statusConfig[status]?.label || status}
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Search - only show if there are tickets */}
      {tickets.length > 0 && (
        <SearchBar
          placeholder="Search tickets by subject, user, or organization..."
          value={searchQuery}
          onChange={setSearchQuery}
        />
      )}

      {/* Table or Empty State */}
      {tickets.length === 0 ? (
        <SupportEmptyState />
      ) : (
        <Table
          columns={columns}
          data={filteredTickets}
          emptyState={
            <div className="py-12">
              <EmptyState
                icon="search"
                title="No matching tickets"
                description="Try adjusting your search or filters."
              />
            </div>
          }
        />
      )}

      {/* Detail Drawer */}
      <TicketDetailDrawer
        ticket={selectedTicket}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
      />
    </div>
  );
};

export default Support;
